import { DEFAULT_CURRENCY } from "@redotech/money/util";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { RedoMarketplaceRpcClientContext } from "@redotech/redo-marketplace-app-common/rpc-client";
import { VendorAuthContext } from "@redotech/redo-marketplace-app-common/vendor-auth";
import { RedoSpinner } from "@redotech/redo-web/arbiter-components/loading/redo-spinner";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { useInfiniteQuery } from "@tanstack/react-query";
import { memo, useContext, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useCart } from "../context";
import { MarketplaceProductListing } from "../marketplace/marketplace-product-listing";

const PRODUCTS_PER_PAGE = 20;

const SORT_OPTIONS = [
  { value: "created_at:desc", label: "Newest" },
  { value: "created_at:asc", label: "Oldest" },
  { value: "name:asc", label: "A-Z" },
  { value: "name:desc", label: "Z-A" },
  { value: "price:asc", label: "Price: Low to high" },
  { value: "price:desc", label: "Price: High to low" },
];

export const MarketplaceProductsGrid = memo(function MarketplaceProductsGrid() {
  const vendorAuth = useContext(VendorAuthContext);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const searchQuery = searchParams.get("q") || undefined;
  const sortParam = searchParams.get("sort") || "created_at:desc";
  const marketplaceRpcClient = useRequiredContext(
    RedoMarketplaceRpcClientContext,
  );
  const { getItem } = useCart();

  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  const [sortBy, sortDirection] = sortParam.split(":") as [
    "price" | "name" | "created_at",
    "asc" | "desc",
  ];

  const {
    data: productsData,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    error: productsError,
    isPending,
    isError,
    isSuccess,
  } = useInfiniteQuery({
    queryKey: [
      "marketplace-products",
      "REDO",
      searchQuery,
      sortBy,
      sortDirection,
    ],
    queryFn: async ({ pageParam }: { pageParam: string | undefined }) => {
      return await marketplaceRpcClient.getMarketplaceProducts({
        marketplaceType: "REDO",
        pagination: { limit: PRODUCTS_PER_PAGE, cursor: pageParam },
        query: searchQuery,
        sortBy,
        sortDirection,
      });
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => {
      return lastPage?.paginationKey;
    },
    enabled: !!vendorAuth,
  });

  const loadMoreRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const loadMoreElement = loadMoreRef.current;

    if (!loadMoreElement) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        // When the load more element is visible and we have more pages to load
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        if (entries[0].isIntersecting && hasNextPage && !isFetching) {
          void fetchNextPage();
        }
      },
      { rootMargin: "200px" },
    );

    observer.observe(loadMoreElement);

    return () => {
      if (loadMoreElement) {
        observer.unobserve(loadMoreElement);
      }
    };
  }, [hasNextPage, isFetching, fetchNextPage]);

  const handleProductClick = useHandler((productId: string) => {
    navigate(`/product/${productId}`);
  });

  const handleSortChange = useHandler(
    (event: React.ChangeEvent<HTMLSelectElement>) => {
      const newParams = new URLSearchParams(searchParams);
      newParams.set("sort", event.target.value);
      setSearchParams(newParams);
    },
  );

  if (isPending) {
    return (
      <Flex align="center" justify="center" p="xl">
        <RedoSpinner />
      </Flex>
    );
  }

  if (isError) {
    return (
      <Flex align="center" justify="center" p="xl">
        <Text color="red">
          Error loading products: {(productsError as Error).message}
        </Text>
      </Flex>
    );
  }

  if (isSuccess) {
    if (!productsData?.pages?.[0]?.products?.length) {
      return (
        <Flex align="center" justify="center" p="xl">
          <Text textColor="tertiary">
            No products available in the marketplace yet.
          </Text>
        </Flex>
      );
    }

    return (
      <div className="max-w-7xl mx-auto w-full">
        <div className="flex items-center justify-between m-6">
          <Text fontSize="xl" fontWeight="bold">
            New items
          </Text>
          <select
            className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            onChange={handleSortChange}
            value={sortParam}
          >
            {SORT_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {/* Product Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 sm:gap-6">
          {productsData.pages.map((page) =>
            page?.products?.map((product) => {
              const primaryVariant = product.variants[0];
              const isInCart = primaryVariant
                ? getItem(product._id, primaryVariant._id) !== undefined
                : false;

              return (
                <MarketplaceProductListing
                  imageUrl={product.images[0]?.url || ""}
                  isInCart={isInCart}
                  key={product._id}
                  onClick={() => handleProductClick(product._id)}
                  price={{
                    amount: primaryVariant?.price ?? 0,
                    currency: DEFAULT_CURRENCY,
                  }}
                  title={product.name}
                />
              );
            }),
          )}
        </div>

        {/* Infinite Scroll Trigger & Loading State */}
        <div className="mt-8" ref={loadMoreRef}>
          <Flex align="center" justify="center" p="lg">
            {isFetchingNextPage ? (
              <Flex align="center" gap="sm">
                <RedoSpinner />
                <Text fontSize="sm" textColor="tertiary">
                  Loading more products...
                </Text>
              </Flex>
            ) : hasNextPage ? (
              <Text fontSize="sm" textColor="tertiary">
                Scroll to load more products...
              </Text>
            ) : (
              <Text fontSize="sm" textColor="tertiary">
                That's all for now! Check back soon for new products.
              </Text>
            )}
          </Flex>
        </div>

        {/* Background refresh indicator */}
        {isFetching && !isFetchingNextPage && (
          <Flex align="center" justify="center" p="sm">
            <RedoSpinner />
          </Flex>
        )}
      </div>
    );
  }

  return null;
});
